import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import api from '../services/api';
import OTPInput from './OTPInput';
import './ConfirmModal.css';
import './CredentialChangeModal.css';

export type CredentialType = 'phone' | 'email';

interface CredentialChangeModalProps {
    credentialType: CredentialType;
    onClose: () => void;
    onSuccess: () => void;
}

/**
 * #2008d — verified change of an account-identity handle (phone/email).
 *
 * Two steps: (1) enter the NEW value → POST /auth/credentials/change-request
 * (sends an OTP to the NEW value); (2) reuse {@link OTPInput} → POST
 * /auth/credentials/change-confirm. A 409 (value already on another account)
 * renders an inline generic message that never names the other account.
 */
const CredentialChangeModal: React.FC<CredentialChangeModalProps> = ({ credentialType, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const isEmail = credentialType === 'email';

    const [step, setStep] = useState<'request' | 'confirm'>('request');
    const [newValue, setNewValue] = useState('');
    const [error, setError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);
    const [expiresAt, setExpiresAt] = useState<number>(0);

    const takenMessage = isEmail
        ? t('settings.contact.emailTaken', 'Diese E-Mail wird bereits verwendet')
        : t('settings.contact.phoneTaken', 'Diese Telefonnummer wird bereits verwendet');

    const handleRequest = async () => {
        const value = newValue.trim();
        if (!value) return;
        setSubmitting(true);
        setError(undefined);
        try {
            const resp = await api.post(
                '/auth/credentials/change-request',
                { credential_type: credentialType, new_value: value },
                { withCredentials: true }
            );
            const expiresIn = resp.data?.expires_in ?? 300;
            setExpiresAt(Date.now() + expiresIn * 1000);
            setStep('confirm');
        } catch (err) {
            const status = (err as AxiosError)?.response?.status;
            if (status === 409) {
                setError(takenMessage);
            } else if (status === 400 || status === 422) {
                setError(
                    isEmail
                        ? t('settings.contact.emailInvalid', 'Ungültige E-Mail-Adresse')
                        : t('settings.contact.phoneInvalid', 'Ungültige Telefonnummer')
                );
            } else {
                setError(t('settings.contact.requestFailed', 'Anfrage fehlgeschlagen. Bitte versuchen Sie es erneut.'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirm = async (code: string) => {
        setSubmitting(true);
        setError(undefined);
        try {
            await api.post(
                '/auth/credentials/change-confirm',
                { credential_type: credentialType, new_value: newValue.trim(), code },
                { withCredentials: true }
            );
            onSuccess();
        } catch (err) {
            const status = (err as AxiosError)?.response?.status;
            setError(
                status === 409 ? takenMessage : t('settings.contact.codeInvalid', 'Ungültiger Code. Bitte erneut versuchen.')
            );
        } finally {
            setSubmitting(false);
        }
    };

    const title = isEmail
        ? t('settings.contact.changeEmailTitle', 'E-Mail ändern')
        : t('settings.contact.changePhoneTitle', 'Telefonnummer ändern');

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{title}</h3>
                </div>
                <div className="modal-body">
                    {step === 'request' ? (
                        <>
                            <label htmlFor="credential-new-value" className="credential-change-label">
                                {isEmail
                                    ? t('settings.contact.newEmail', 'Neue E-Mail')
                                    : t('settings.contact.newPhone', 'Neue Telefonnummer')}
                            </label>
                            <input
                                id="credential-new-value"
                                className="credential-change-input"
                                type={isEmail ? 'email' : 'tel'}
                                value={newValue}
                                onChange={e => setNewValue(e.target.value)}
                                placeholder={isEmail ? 'name@example.de' : '+49...'}
                                autoFocus
                            />
                        </>
                    ) : (
                        <>
                            <p className="credential-change-sent">
                                {t('settings.contact.codeSentTo', {
                                    defaultValue: 'Code gesendet an {{value}}',
                                    value: newValue.trim(),
                                })}
                            </p>
                            <OTPInput length={6} onComplete={handleConfirm} expiresAt={expiresAt} />
                        </>
                    )}
                    {error && (
                        <p className="credential-change-error" role="alert">
                            {error}
                        </p>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="modal-btn modal-btn-cancel" onClick={onClose}>
                        {t('common.cancel', 'Abbrechen')}
                    </button>
                    {step === 'request' && (
                        <button
                            className="modal-btn modal-btn-confirm modal-btn-info"
                            onClick={handleRequest}
                            disabled={submitting || !newValue.trim()}
                        >
                            {t('settings.contact.sendCode', 'Code senden')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CredentialChangeModal;
